import { initialAuthState, type AuthState } from '@/features/auth/store/authSlice';

const authStorageKey = 'aarogyaflow:auth-state';
const storageVersion = 2;

type PersistedAuthState = {
  version: number;
  state: AuthState;
};

function loadFromStorage(storage: Storage | null): AuthState | null {
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(authStorageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;
    const persistedState: Partial<AuthState> =
      parsed && typeof parsed === 'object' && 'state' in parsed && parsed.state && typeof parsed.state === 'object'
        ? (parsed.state as Partial<AuthState>)
        : ((parsed ?? {}) as Partial<AuthState>);

    return {
      ...initialAuthState,
      ...(persistedState as Partial<AuthState>),
      rememberMe:
        typeof persistedState.rememberMe === 'boolean'
          ? persistedState.rememberMe
          : storage === window.localStorage
    };
  } catch {
    return null;
  }
}

function persistToStorage(storage: Storage, state: AuthState) {
  storage.setItem(
    authStorageKey,
    JSON.stringify({
      version: storageVersion,
      state: {
        userId: state.userId,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAtUtc: state.expiresAtUtc,
        userName: state.userName,
        userEmail: state.userEmail,
        role: state.role,
        permissions: state.permissions,
        clinicId: state.clinicId,
        isAuthenticated: state.isAuthenticated,
        rememberMe: state.rememberMe
      } satisfies AuthState
    } satisfies PersistedAuthState)
  );
}

export function loadAuthState(): AuthState {
  if (typeof window === 'undefined') {
    return initialAuthState;
  }

  return loadFromStorage(window.localStorage) ?? loadFromStorage(window.sessionStorage) ?? initialAuthState;
}

export function saveAuthState(state: AuthState) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const activeStorage = state.rememberMe ? window.localStorage : window.sessionStorage;
    const inactiveStorage = state.rememberMe ? window.sessionStorage : window.localStorage;

    persistToStorage(activeStorage, state);
    inactiveStorage.removeItem(authStorageKey);
  } catch {
    // Ignore storage failures.
  }
}

export function clearAuthState() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(authStorageKey);
    window.sessionStorage.removeItem(authStorageKey);
  } catch {
    // Ignore storage failures.
  }
}
